/**
 * PrepareCallModal — Sprint 2 V3
 * ===============================
 * Bouton + modale affichant la fiche de préparation Claude pour un point vendeur.
 * Cache 1h backend, donc cliquable plusieurs fois sans surcoût.
 */
import { useState } from 'react';
import axios from 'axios';
import { Phone, X, Copy, AlertCircle, MessageSquare, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import AIThinkingAnimation from '../ai/AIThinkingAnimation';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PrepareCallButton({ mandateId, sellerName, variant = 'default' }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const fetchPreparation = async () => {
        try {
            setLoading(true);
            setError(null);
            setOpen(true); // Open modal immediately to show loading animation
            const token = localStorage.getItem('token');
            const res = await axios.post(
                `${API}/api/ai/prepare-call/${mandateId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
            );
            setData(res.data);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            setError(msg);
            toast.error(`Préparation impossible : ${msg}`);
            setOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const copyScript = () => {
        if (!data?.suggested_opening_script) return;
        navigator.clipboard.writeText(data.suggested_opening_script);
        toast.success('Script copié dans le presse-papier');
    };

    return (
        <>
            <Button
                onClick={fetchPreparation}
                disabled={loading}
                variant={variant === 'default' ? 'default' : 'outline'}
                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white shadow-sm"
                data-testid="prepare-call-button"
            >
                <Phone className="w-4 h-4 mr-2" />
                {loading ? 'Préparation IA...' : 'Préparer mon point'}
                <Sparkles className="w-3 h-3 ml-2 opacity-70" />
            </Button>

            {open && (
                <div
                    className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4 animate-in fade-in duration-200"
                    onClick={() => !loading && setOpen(false)}
                    data-testid="prepare-call-modal"
                >
                    <div
                        className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in slide-in-from-bottom md:zoom-in duration-300"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white p-5 rounded-t-3xl">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Sparkles className="w-4 h-4" />
                                        <span className="text-xs uppercase tracking-wider font-bold">Préparation point vendeur</span>
                                    </div>
                                    <h2 className="text-xl font-bold">{data?.seller_name || sellerName || 'Préparation en cours…'}</h2>
                                    {data && (
                                        <p className="text-xs opacity-80 mt-1">
                                            Propulsé par Coach · IA Homly · Risque {data.risk_score}/100 ({data.risk_label})
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => !loading && setOpen(false)}
                                    className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-30"
                                    disabled={loading}
                                    data-testid="prepare-call-close"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Loading state — Animation cinématique */}
                        {loading && (
                            <div className="p-5">
                                <AIThinkingAnimation />
                                <p className="text-center text-xs text-slate-500 mt-4">
                                    L'IA analyse les KPI de ce mandat pour vous préparer le point parfait…
                                </p>
                            </div>
                        )}

                        {!loading && data && (
                        <>
                        <div className="p-5 space-y-5">
                            {/* Context summary */}
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Contexte mandat</h3>
                                <p className="text-sm text-slate-800 leading-relaxed">{data.context_summary}</p>
                            </div>

                            {/* Risk factors */}
                            {data.risk_factors && data.risk_factors.length > 0 && (
                                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-orange-700 mb-2 flex items-center gap-1">
                                        <AlertCircle className="w-3.5 h-3.5" /> Facteurs de risque détectés
                                    </h3>
                                    <ul className="space-y-1.5">
                                        {data.risk_factors.map((f, i) => (
                                            <li key={i} className="text-sm text-slate-800 flex gap-2">
                                                <span className="text-orange-500 shrink-0">•</span>
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Pain points */}
                            {data.pain_points && data.pain_points.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Inquiétudes vendeur</h3>
                                    <ul className="space-y-2">
                                        {data.pain_points.map((p, i) => (
                                            <li key={i} className="text-sm text-slate-800 flex gap-2">
                                                <span className="text-amber-500 shrink-0">⚠️</span>
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Talking points */}
                            {data.talking_points && data.talking_points.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1">
                                        <MessageSquare className="w-3.5 h-3.5" /> 3 sujets à aborder
                                    </h3>
                                    <div className="space-y-3">
                                        {data.talking_points.map((t, i) => (
                                            <div key={i} className="bg-white border-l-4 border-[#681793] pl-4 py-2">
                                                <div className="flex items-start gap-2 mb-1">
                                                    <span className="bg-[#681793] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                                                    <h4 className="text-sm font-bold text-slate-900">{t.title}</h4>
                                                </div>
                                                <p className="text-sm text-slate-700 ml-7 mb-1">
                                                    <strong className="text-xs uppercase text-slate-500">À dire :</strong>{' '}
                                                    <span className="italic">"{t.what_to_say}"</span>
                                                </p>
                                                {t.expected_response && (
                                                    <p className="text-xs text-slate-500 ml-7">
                                                        <strong>Réponse attendue :</strong> {t.expected_response}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Next step */}
                            {data.suggested_next_step && (
                                <div className="bg-slate-900 text-white rounded-xl p-4">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1">
                                        <ChevronRight className="w-3.5 h-3.5" /> Prochaine étape à proposer
                                    </h3>
                                    <p className="text-sm">{data.suggested_next_step}</p>
                                </div>
                            )}

                            {/* Opening script */}
                            {data.suggested_opening_script && (
                                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 relative">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Script d'ouverture suggéré</h3>
                                    <p className="text-sm text-slate-800 italic leading-relaxed pr-12">"{data.suggested_opening_script}"</p>
                                    <button
                                        onClick={copyScript}
                                        className="absolute top-3 right-3 p-2 bg-white hover:bg-emerald-100 rounded-lg transition-colors text-emerald-700 shadow-sm"
                                        title="Copier le script"
                                        data-testid="prepare-call-copy"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 text-center">
                            <p className="text-xs text-slate-500">
                                Cette préparation est générée à partir des données réelles de votre mandat.{' '}
                                {data.from_cache && <span className="text-slate-400">· Cache 1h actif</span>}
                            </p>
                        </div>
                        </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
